import { Client, type StompSubscription } from "@stomp/stompjs";
import { useSnackStore } from "@/stores/snackStore";
import { logError } from "@/utils/AppError";
import { ref } from "vue";

// if modified here also modify in tests/utils/auth.ts
declare global {
  interface Window {
    __stompClient?: Client;
    __subscriptions?: Map<string, ChannelHandler>;
  }
}
// if modified here also modify in tests/utils/auth.ts
type ChannelHandler = {
  channel: string;
  handler: (body: string) => void;
  subscription?: StompSubscription;
};

const CLIENT_PARAMS = {
  brokerURL: import.meta.env.VITE_WS_BASE,
  reconnectDelay: 5000,
  heartbeatIncoming: 10_000,
  heartbeatOutgoing: 10_000,
};

/** Failures while the tab is active before we bother the user (~15s with 5s reconnect). */
const WS_ERROR_SNACK_MIN_FAILURES = 3;
/** Do not re-snack for a long time even if reconnect flaps (and never reset this on connect). */
const WS_ERROR_SNACK_COOLDOWN_MS = 120_000;
/** After tab wake/unfreeze, suppress snacks while STOMP settles. */
const WS_WAKE_GRACE_MS = 15_000;

const WS_RECONNECT_NOTICE = "Realtime connection interrupted — reconnecting automatically…";

let stompClient: Client | null = null;
const isConnected = ref<boolean>(false);
const isConnecting = ref<boolean>(false);
const currentToken = ref<string | null>(null);
const isDeactivating = ref<boolean>(false); // guard to avoid re-entrancy during deactivate
const wsFailureCount = ref<number>(0);
const lastWsErrorSnackAt = ref<number>(0);
let lastBecameVisibleAt = typeof document === "undefined" ? 0 : Date.now();
let pageLifecycleListenersAttached = false;

/**
 * Stores all active subscriptions to survive reconnections.
 *
 * When connection is established (onConnect), all subscriptions in this Map
 * are automatically reactivated, ensuring no messages are lost during
 * token refresh or network interruptions.
 *
 * The Map also allows subscribing before connection is ready (deferred subscription).
 */
const subscriptions = new Map<string, ChannelHandler>();

/** Registered channels, including deferred ones. Read by browser diagnostics. */
export const getWebsocketSubscriptionCount = () => subscriptions.size;

const markPageAwake = () => {
  lastBecameVisibleAt = Date.now();
};

const ensurePageLifecycleTracking = () => {
  if (pageLifecycleListenersAttached || typeof document === "undefined") return;
  pageLifecycleListenersAttached = true;

  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") markPageAwake();
  });

  // Chromium Page Lifecycle: start grace when the tab unfreezes so the
  // reconnect storm after wake stays silent.
  globalThis.addEventListener("resume", markPageAwake);
};

const shouldShowWsReconnectSnack = (): boolean => {
  if (typeof document !== "undefined" && document.visibilityState === "hidden") {
    return false;
  }

  const now = Date.now();
  if (now - lastBecameVisibleAt < WS_WAKE_GRACE_MS) return false;
  if (wsFailureCount.value < WS_ERROR_SNACK_MIN_FAILURES) return false;
  if (now - lastWsErrorSnackAt.value < WS_ERROR_SNACK_COOLDOWN_MS) return false;
  return true;
};

const maybeShowWsReconnectSnack = (snackStore: ReturnType<typeof useSnackStore>) => {
  if (!shouldShowWsReconnectSnack()) return;
  lastWsErrorSnackAt.value = Date.now();
  snackStore.showSnack({ message: WS_RECONNECT_NOTICE, color: "error" }, false);
};

const reactivateAllSubscriptions = () => {
  for (const [channel, subscription] of subscriptions) {
    subscription.subscription = stompClient!.subscribe(channel, (message) => {
      subscription.handler(message.body);
    });
  }
};

const unsubscribeAll = () => {
  for (const subscription of subscriptions.values()) {
    subscription.subscription?.unsubscribe();
    subscription.subscription = undefined;
  }
};

const unsubscribe = (channel: string) => {
  const channelHandler = subscriptions.get(channel) as ChannelHandler;
  channelHandler?.subscription?.unsubscribe();
  subscriptions.delete(channel);
};

export const useWebsocket = () => {
  const snackStore = useSnackStore();

  const setupConnection = (token: string) => {
    if (!token) return;
    if ((isConnected.value || isConnecting.value) && currentToken.value === token) return;
    if (stompClient?.active || isDeactivating.value) return;

    ensurePageLifecycleTracking();
    isConnecting.value = true;
    currentToken.value = token;

    stompClient = new Client({
      ...CLIENT_PARAMS,
      connectHeaders: {
        authorization: `${token}`,
      },

      onConnect: () => {
        try {
          reactivateAllSubscriptions();

          isConnected.value = true;
          // Reset streak for "persistent outage" detection, but keep snack cooldown
          // so a fail→reconnect flap after wake cannot spam the UI.
          wsFailureCount.value = 0;
        } catch (error) {
          logError(error, {
            context: {
              feature: "websocket",
              step: "onConnect",
            },
          });
          snackStore.showError(error);
          disconnect();
        } finally {
          isConnecting.value = false;
        }
      },

      onStompError: (frame) => {
        snackStore.showError(frame.body);
        isConnecting.value = false;
      },

      onWebSocketError: () => {
        wsFailureCount.value += 1;
        console.warn("⚠️ WS connection error, retrying automatically...", {
          failures: wsFailureCount.value,
        });
        maybeShowWsReconnectSnack(snackStore);
        isConnecting.value = false;

        // In rare cases the client can become inactive and stop retrying.
        // If that happens, re-activate it to keep reconnection alive.
        if (stompClient && !stompClient.active && !isDeactivating.value) {
          stompClient.activate();
        }
      },

      onWebSocketClose: (event) => {
        // Ignore close events triggered by our own deactivate()
        if (isDeactivating.value) return;

        const isExpectedClose = event.code === 1000 && event.wasClean;
        if (!isExpectedClose) {
          wsFailureCount.value += 1;
          console.warn("⚠️ WS connection closed unexpectedly", {
            code: event.code,
            reason: event.reason,
            wasClean: event.wasClean,
            failures: wsFailureCount.value,
          });
        }
        isConnected.value = false;
      },
    });

    stompClient.activate();
  };

  const updateConnection = async (token: string) => {
    if (currentToken.value === token && (isConnected.value || isConnecting.value)) {
      return;
    }

    isConnecting.value = true;

    try {
      await disconnect();
      setupConnection(token);
    } catch (error) {
      logError(error, {
        context: {
          feature: "websocket",
          step: "updateConnection",
        },
      });
      snackStore.showError(error);
    } finally {
      if (!isConnected.value) {
        isConnecting.value = false;
      }
    }
  };

  const disconnect = async () => {
    isDeactivating.value = true; // guard re-entrant close callbacks
    unsubscribeAll();

    if (stompClient) {
      try {
        await stompClient.deactivate();
      } catch (error) {
        logError(error, {
          context: {
            feature: "websocket",
            step: "disconnect",
          },
        });
        console.error("❌ Error disconnecting from WS:", error);
      } finally {
        stompClient = null;
      }
    }

    isConnected.value = false;
    isConnecting.value = false;
    currentToken.value = null;
    isDeactivating.value = false;
  };

  const subscribe = (channel: string, handler: (body: string) => void) => {
    const channelHandler: ChannelHandler = { channel, handler };
    subscriptions.set(channel, channelHandler);

    if (isConnected.value && stompClient) {
      channelHandler.subscription = stompClient.subscribe(channel, (message) => {
        handler(message.body);
      });
    }

    return () => {
      const channelHandler = subscriptions.get(channel) as ChannelHandler;
      channelHandler?.subscription?.unsubscribe();
      subscriptions.delete(channel);
    };
  };

  return {
    isConnected,
    isConnecting,
    setupConnection,
    updateConnection,
    disconnect,
    subscribe,
    unsubscribe,
  };
};
